import {
	DocumentFormat,
	DocumentFormatValues,
	DocumentGenerationConfig,
	DocumentTemplate,
	GENERATE_DOCUMENT_TYPE_CONFIG,
	ProjectDocumentGenerationTypeKeys,
	type PropositionProductTypeValues,
} from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Label,
	RadioGroup,
	RadioGroupItem,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { useQueryClient } from "@tanstack/react-query";
import { FileCode, FileText } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
	useGenerateDocument,
	useGenerateMultipleDocuments,
} from "../../../../api/mutations/propositions.mutations";
import { useGetDocumentTemplates } from "../../../../api/queries/documents.queries";
import { useProposition } from "../../../../api/queries/propositions.queries";
import {
	useGetDuneSignatories,
	useMe,
} from "../../../../api/queries/users.queries";
import { tasksQueryKeys } from "../../../../api/query-keys/tasks.query-keys";
import { propositionsQueryKeys } from "../../../../api/query-keys/propositions.query-keys";

interface GenerateDocumentModalProps {
	type: ProjectDocumentGenerationTypeKeys | ProjectDocumentGenerationTypeKeys[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
	propositionId: number;
	onSuccess?: (data?: {
		documentUrl: string;
		documentId: string;
		fileId: string;
		message: string;
	}) => void;
}

const GenerateDocumentModal: React.FC<GenerateDocumentModalProps> = ({
	type,
	open,
	onOpenChange,
	propositionId,
	onSuccess,
}) => {
	const isMultipleTypes = Array.isArray(type);
	const types = isMultipleTypes ? type : [type];

	const [documentConfigs, setDocumentConfigs] = useState<
		Record<
			ProjectDocumentGenerationTypeKeys,
			{ templateId: string; format: DocumentFormatValues }
		>
	>(() => {
		const initial: Record<
			string,
			{ templateId: string; format: DocumentFormatValues }
		> = {};
		for (const t of types) {
			initial[t] = { templateId: "", format: DocumentFormat.PDF };
		}
		return initial;
	});

	const [selectedSignatoryId, setSelectedSignatoryId] = useState<string>("");

	const firstType = types[0];
	const config = isMultipleTypes
		? {
				title: "Générer des documents",
				description:
					"Sélectionnez les modèles, le signataire Dune et les formats de documents",
				buttonLabel: "Générer les documents",
			}
		: GENERATE_DOCUMENT_TYPE_CONFIG[firstType];

	const { data: allTemplates, isLoading: isLoadingTemplates } =
		useGetDocumentTemplates();

	const { data: duneSignatories, isLoading: isLoadingSignatories } =
		useGetDuneSignatories();

	const { data: proposition, isLoading: isLoadingProposition } =
		useProposition(propositionId);

	const { me } = useMe();

	const getTemplateTypeForProductType = useCallback(
		(
			docType: ProjectDocumentGenerationTypeKeys,
			productType: PropositionProductTypeValues,
		) => {
			const typeConfig = GENERATE_DOCUMENT_TYPE_CONFIG[docType];
			return typeConfig.getTemplateType?.(productType);
		},
		[],
	);

	const getValidTemplatesForType = useCallback(
		(docType: ProjectDocumentGenerationTypeKeys) => {
			if (!allTemplates || !proposition?.productType) return [];

			const expectedTemplateType = getTemplateTypeForProductType(
				docType,
				proposition.productType,
			);

			return allTemplates.filter(
				(template: DocumentTemplate) =>
					template.validated && template.templateType === expectedTemplateType,
			);
		},
		[allTemplates, proposition?.productType, getTemplateTypeForProductType],
	);

	useEffect(() => {
		if (me && duneSignatories && !selectedSignatoryId) {
			const currentUserSignatory = duneSignatories.find(
				(signatory) => signatory.id === me.id,
			);
			if (currentUserSignatory) {
				setSelectedSignatoryId(currentUserSignatory.id.toString());
			}
		}
	}, [me, duneSignatories, selectedSignatoryId]);

	const queryClient = useQueryClient();
	const singleMutation = useGenerateDocument(propositionId);
	const multiMutation = useGenerateMultipleDocuments(propositionId);

	const handleGenerate = async () => {
		if (!selectedSignatoryId) {
			toast.error("Veuillez sélectionner un signataire Dune");
			return;
		}

		if (isMultipleTypes) {
			const missingTemplates = types.filter(
				(t) => !documentConfigs[t]?.templateId,
			);
			if (missingTemplates.length > 0) {
				toast.error("Veuillez sélectionner un modèle pour chaque document");
				return;
			}

			const documentsToGenerate: DocumentGenerationConfig[] = types.map(
				(t) => ({
					type: t,
					templateId: documentConfigs[t].templateId,
					format: documentConfigs[t].format,
				}),
			);

			multiMutation.mutate(
				{
					documents: documentsToGenerate,
					signataireDuneId: Number(selectedSignatoryId),
				},
				{
					onSuccess: async (data) => {
						toast.success(data.message);
						await Promise.all([
							queryClient.invalidateQueries({
								queryKey: propositionsQueryKeys.tasks(propositionId),
								refetchType: "active",
							}),
							queryClient.invalidateQueries({
								queryKey: tasksQueryKeys.activeTasksList(),
								refetchType: "active",
							}),
						]);
						onOpenChange(false);
						if (onSuccess && data.documents[0]) {
							onSuccess({
								documentUrl: data.documents[0].documentUrl,
								documentId: data.documents[0].documentId.toString(),
								fileId: data.documents[0].fileId.toString(),
								message: data.message,
							});
						}
					},
					onError: (error: unknown) => {
						const message =
							error instanceof Error
								? error.message
								: "Erreur lors de la génération";
						toast.error(message);
					},
				},
			);
		} else {
			if (!documentConfigs[firstType]?.templateId) {
				toast.error(
					config.templateErrorMessage || "Veuillez sélectionner un modèle",
				);
				return;
			}

			singleMutation.mutate(
				{
					type: firstType,
					templateId: documentConfigs[firstType].templateId,
					signataireDuneId: Number(selectedSignatoryId),
					format: documentConfigs[firstType].format,
				},
				{
					onSuccess: async (data) => {
						toast.success(
							config.successMessage || "Document généré avec succès",
						);
						await Promise.all([
							queryClient.invalidateQueries({
								queryKey: propositionsQueryKeys.tasks(propositionId),
								refetchType: "active",
							}),
							queryClient.invalidateQueries({
								queryKey: tasksQueryKeys.activeTasksList(),
								refetchType: "active",
							}),
						]);
						onOpenChange(false);
						if (onSuccess) {
							onSuccess({
								...data,
								documentId: data.documentId.toString(),
								fileId: data.fileId.toString(),
							});
						}
					},
					onError: (error: unknown) => {
						const message =
							error instanceof Error
								? error.message
								: "Erreur lors de la génération";
						toast.error(message);
					},
				},
			);
		}
	};

	const formatOptions = [
		{
			value: DocumentFormat.PDF,
			label: "PDF",
			description: "Pour signature électronique",
			icon: FileText,
		},
		{
			value: DocumentFormat.DOCX,
			label: "Word",
			description: "Pour permettre la modification",
			icon: FileCode,
		},
	];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{config.title}</DialogTitle>
					<DialogDescription>{config.description}</DialogDescription>
				</DialogHeader>

				<div className="space-y-6 py-4">
					{types.map((docType) => {
						const typeConfig = GENERATE_DOCUMENT_TYPE_CONFIG[docType];
						const validTemplates = getValidTemplatesForType(docType);

						return (
							<div key={docType} className="space-y-4 border rounded-lg p-4">
								<h3 className="font-medium text-sm">
									{typeConfig.title || docType}
								</h3>

								<div className="space-y-2">
									<label
										htmlFor={`template-select-${docType}`}
										className="text-sm font-medium"
									>
										{typeConfig.templateLabel || "Modèle"}
									</label>
									<Select
										value={documentConfigs[docType]?.templateId || ""}
										onValueChange={(value) =>
											setDocumentConfigs((prev) => ({
												...prev,
												[docType]: {
													...prev[docType],
													templateId: value,
												},
											}))
										}
										disabled={
											isLoadingTemplates ||
											isLoadingProposition ||
											validTemplates.length === 0
										}
									>
										<SelectTrigger id={`template-select-${docType}`}>
											<SelectValue
												placeholder={
													isLoadingTemplates || isLoadingProposition
														? "Chargement..."
														: validTemplates.length === 0
															? "Aucun modèle disponible"
															: "Sélectionnez un modèle"
												}
											/>
										</SelectTrigger>
										<SelectContent>
											{validTemplates.map((template: DocumentTemplate) => (
												<SelectItem key={template.id} value={template.id}>
													{template.name} (v{template.version})
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<fieldset className="space-y-2">
									<legend className="text-sm font-medium">
										Format du document
									</legend>
									<RadioGroup
										value={
											documentConfigs[docType]?.format || DocumentFormat.PDF
										}
										onValueChange={(value) =>
											setDocumentConfigs((prev) => ({
												...prev,
												[docType]: {
													...prev[docType],
													format: value as DocumentFormatValues,
												},
											}))
										}
										className="grid grid-cols-2 gap-3"
									>
										{formatOptions.map((option) => {
											const Icon = option.icon;
											return (
												<Label
													key={`${docType}-${option.value}`}
													htmlFor={`${docType}-${option.value}`}
													className="relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-input p-4 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
												>
													<RadioGroupItem
														id={`${docType}-${option.value}`}
														value={option.value}
														className="absolute top-2 right-2"
													/>
													<Icon className="h-6 w-6" />
													<span className="font-medium text-xs text-center">
														{option.label}
													</span>
												</Label>
											);
										})}
									</RadioGroup>
								</fieldset>
							</div>
						);
					})}

					<div className="space-y-2">
						<label htmlFor="signatory-select" className="text-sm font-medium">
							Signataire Dune
						</label>
						<Select
							value={selectedSignatoryId}
							onValueChange={setSelectedSignatoryId}
							disabled={isLoadingSignatories || !duneSignatories?.length}
						>
							<SelectTrigger id="signatory-select">
								<SelectValue
									placeholder={
										isLoadingSignatories
											? "Chargement..."
											: !duneSignatories?.length
												? "Aucun signataire disponible"
												: "Sélectionnez un signataire"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{duneSignatories?.map((signatory) => (
									<SelectItem
										key={signatory.id}
										value={signatory.id.toString()}
									>
										{signatory.firstname} {signatory.lastname}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleGenerate}
						disabled={
							!selectedSignatoryId ||
							types.some(
								(t) =>
									!documentConfigs[t]?.templateId ||
									getValidTemplatesForType(t).length === 0,
							) ||
							singleMutation.isPending ||
							multiMutation.isPending
						}
						isLoading={singleMutation.isPending || multiMutation.isPending}
					>
						{config.buttonLabel || "Générer"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default GenerateDocumentModal;
